import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, PageHeader, Select, Skeleton, useConfirm, useToast } from '../ui';
import { RefreshIcon, TrashIcon } from '../ui/icons';
import { useLeaveGuard } from '../shell/changes';
import { itemSummary } from './collection/itemSummary';
import { fieldOptionLabel, presetSources, optionValue, uiLang, usedFields } from '../kinds';
import { SOURCE_SCHEMES } from '../sources';
import { LOCATION_KEY, fieldKey, isPreservedKey } from '../fieldKeys';
import { fieldErrorMessage, readCode, sameValue } from '../fieldTypes';
import { TagsInput, VocabInput, LinkInput, RatingInput, YesNoInput, MoneyInput, MeasureInput, IdentifierInput, ImageInput } from './FieldInputs';
import api, { mediaUrl } from '../config/api';
import LocationPicker from './LocationPicker';
import SourceWarnings from './SourceWarnings';
import { locationErrorMessage, useLocations } from '../locations';
import { errorMessage } from '../errors';
import { codeOfItem, useFindDetails } from './item/findDetails';
import TextSearchDialog from '../scan/TextSearchDialog';
import { sourceError } from './TmdbSearch';
import { sourceLabel, useSources } from '../sources';

/** No value: nothing typed, no tag, money or measure without its number. false (no) is a value. */
const isEmptyValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string') return !value.trim();
  if (typeof value === 'object') {
    const inner = 'amount' in value ? value.amount : value.value;
    return inner === null || inner === undefined || String(inner).trim() === '';
  }
  return false;
};

/** The message for a refused refresh from TMDB, by the API's code. */
export const refreshError = (t, error) => {
  const code = error?.response?.data?.code;
  if (['source_busy', 'source_not_found', 'source_unavailable', 'source_error', 'rate_limited', 'client_outdated'].includes(code)) {
    return t(`addFromSource.errors.${code}`);
  }
  if (code === 'no_source_id') return t('itemForm.refresh.noTmdbId');
  if (code === 'conversion_running') return t('itemForm.conversionRunning');
  return t('addFromSource.errors.generic', { error: errorMessage(t, error) });
};

// Roles shown first, under "Main information"; images get their own section.
const MAIN_ROLES = ['title', 'subtitle', 'creators', 'year', 'series', 'series_number', 'description'];

/** A field of the form: label (linked to its input, or naming its group), help, error. */
function ItemField({ id, label, required, hint, error, simple, children }) {
  const { t } = useTranslation();
  const labelId = `${id}-label`;
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={`item-field${error ? ' has-error' : ''}`}>
      {simple
        ? <label id={labelId} htmlFor={id} className="ui-field__label">{label}{required && <span className="item-field__required" aria-hidden="true"> *</span>}</label>
        : <span id={labelId} className="ui-field__label">{label}{required && <span className="item-field__required" aria-hidden="true"> *</span>}</span>}
      {required && <span className="visually-hidden">{t('itemForm.requiredField')}</span>}
      {hint && <p id={`${id}-hint`} className="ui-field__hint">{hint}</p>}
      {simple ? children : (
        <div role="group" aria-labelledby={labelId} aria-describedby={describedBy} className="item-field__group">{children}</div>
      )}
      {error && <p id={`${id}-error`} role="alert" className="ui-field__error">{error}</p>}
    </div>
  );
}

const SIMPLE_TYPES = ['text', 'textarea', 'number', 'date', 'select'];

/** The values that hold something, by key in a stable order: what an edit is measured on. */
const filled = (values) => Object.keys(values || {}).sort()
  .filter(key => !isEmptyValue(values[key]))
  .map(key => [key, values[key]]);

function ItemForm({ inventory, item, selectedItemType, availableItemTypes, onSave, onClose, onDelete, onItemTypeChange, onRefreshed, initialValues: prefill }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [fieldValues, setFieldValues] = useState({});
  const [currentItemType, setCurrentItemType] = useState(null);
  // A value the server refused (400 invalid_value), by field key
  const [fieldErrors, setFieldErrors] = useState({});
  // A refused value of a field the form does not show (another type's, say)
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Image fields uploading right now, by key: the item is saved once they are done.
  const [uploading, setUploading] = useState({});
  const busy = Object.values(uploading).some(Boolean);
  // The item's place (items.location_id), chosen in the collection's location tree.
  const hasLocations = !!inventory.location_type_id;
  const { places, reload: reloadPlaces } = useLocations(inventory.location_type_id);
  const [locationId, setLocationId] = useState(item?.location_id ?? null);
  useEffect(() => { setLocationId(item?.location_id ?? null); }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The owner (of the collection, so of its location type) may add a place from here.
  const canAddPlace = hasLocations && inventory.my_permission === 'owner';
  const addPlace = async (parentId, name) => {
    try {
      const response = await api.post(`/location-types/${inventory.location_type_id}/locations`, { name, parent_id: parentId });
      await reloadPlaces();
      return response.data;
    } catch (error) {
      throw locationErrorMessage(t, error);
    }
  };

  const prefillRef = useRef(prefill);

  // Unsaved edits: the values (and place, and type) differ from what was loaded or last saved.
  const baseline = useRef(null);   // {values, typeId} once the form is filled

  // The type: the one chosen (in this form or the "which type?" dialog — the
  // collection passes the item's own type until then), else the item's, else
  // the default. Taking the item's first undid a type changed in the form.
  // Read through a ref: the effect follows the item's identity (its id), not every new copy of it.
  const itemRef = useRef(item);
  itemRef.current = item;
  useEffect(() => {
    const item = itemRef.current;
    const known = (type) => type && (availableItemTypes || []).some(other => String(other.id) === String(type.id));
    let initialItemType = known(selectedItemType) ? selectedItemType : null;
    if (!initialItemType && item && item.item_type_id && availableItemTypes) {
      initialItemType = availableItemTypes.find(type => String(type.id) === String(item.item_type_id));
    }
    if (!initialItemType) {
      initialItemType = selectedItemType || inventory.default_item_type;
    }
    setCurrentItemType(initialItemType);
  }, [item?.id, selectedItemType, availableItemTypes, inventory.default_item_type]);

  useEffect(() => {
    // Use currentItemType fields if available, otherwise fall back to inventory fields
    const fieldsToUse = currentItemType?.fields || inventory.fields || [];
    
    // Always initialize ALL fields defined in the item type
    const initialValues = {};
    fieldsToUse.forEach(field => {
      initialValues[fieldKey(field)] = '';
    });
    // If editing an existing item, overlay the existing values
    if (item && item.field_values) {
      Object.keys(item.field_values).forEach(key => {
        // Fields of the current item type, plus values without a field (x_ orphans) to keep them
        if (initialValues.hasOwnProperty(key) || isPreservedKey(key)) {
          initialValues[key] = item.field_values[key];
        }
      });
    }
    
    // A new item opened from the scanner carries its code (phase 4 §5.6): an edit, not the saved state.
    const blank = { ...initialValues };
    if (!item && prefillRef.current) {
      Object.keys(prefillRef.current).forEach((key) => {
        if (initialValues.hasOwnProperty(key)) initialValues[key] = prefillRef.current[key];
      });
    }
    setFieldValues(initialValues);
    // The first values of the item (or of a new one) are the saved state; a type
    // changed in the form is an edit, so the baseline is only taken once.
    if (baseline.current === null && currentItemType) baseline.current = { values: blank, typeId: currentItemType.id };
  }, [item, currentItemType, inventory.fields]);

  const handleFieldChange = (fieldName, value) => {
    setFieldValues(prevValues => ({
      ...prevValues,
      [fieldName]: value
    }));
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleItemTypeChange = (newItemType) => {
    setCurrentItemType(newItemType);
    if (onItemTypeChange) {
      onItemTypeChange(newItemType);
    }
    
    // When changing item type, preserve values for fields that exist in both types
    // Keys are per type: a value carries over when the new type has a field with the same key
    const newValues = {};
    Object.keys(fieldValues).forEach(key => {
      if (isPreservedKey(key)) {
        newValues[key] = fieldValues[key];
      }
    });
    newItemType.fields.forEach(field => {
      const key = fieldKey(field);
      newValues[key] = fieldValues[key] !== undefined ? fieldValues[key] : '';
    });

    setFieldValues(newValues);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields (an unused field is never required: the server refuses that)
    const fieldsToUse = usedFields(currentItemType?.fields || inventory.fields || []);
    const missingRequired = fieldsToUse
      .filter(field => {
        if (!field.required) return false;
        
        const value = fieldValues[fieldKey(field)];
        
        // For checkbox fields, check if at least one option is selected
        if (field.type === 'checkbox') {
          return !Array.isArray(value) || value.length === 0;
        }
        
        // For other fields, check if value exists and is not empty
        return isEmptyValue(value);
      })
      .map(field => field.name);

    if (missingRequired.length > 0) {
      toast.error(t('itemForm.missingRequired', { fields: missingRequired.join(', ') }));
      const missing = fieldsToUse.filter(field => missingRequired.includes(field.name));
      setFieldErrors(Object.fromEntries(missing.map(field => [fieldKey(field), t('itemForm.required')])));
      const first = missing[0] && document.getElementById(`item-field-${fieldKey(missing[0])}`);
      if (first && first.focus) first.focus();
      return;
    }

    const itemTypeId = currentItemType?.id || inventory.default_item_type?.id || inventory.inventory_type_id;
    
    // The guard stays up while saving: until the server says yes, the edits are
    // not saved, and leaving still asks. On success the collection closes the form.
    setSaving(true);
    try {
      await onSave({
        field_values: fieldValues,
        item_type_id: itemTypeId,
        ...(hasLocations ? { location_id: locationId } : {}),
      });
    } catch (error) {
      // The server names the field it refused: show the message on it.
      const data = error.response?.data || {};
      if (data.code === 'conversion_running') {
        // The type is being converted to a model: its items wait until it is done.
        setFieldErrors({});
        setFormError(t('itemForm.conversionRunning'));
      }
      if (data.code === 'invalid_value') {
        const message = data.field === LOCATION_KEY ? t('locations.errors.invalid_place') : fieldErrorMessage(t, error, fieldsToUse);
        if (data.field && (fieldsToUse.some(field => fieldKey(field) === data.field) || (data.field === LOCATION_KEY && hasLocations))) {
          setFieldErrors({ [data.field]: message });
          setFormError(null);
        } else {
          setFieldErrors({});
          setFormError(message);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  // An item of a film or series type that knows its TMDB id can be filled again from TMDB (the type's integration
  // decides which fields, and whether filled ones are overwritten). The server writes; the form takes what changed.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);   // {changed: [names]} or {error}
  // The server reads the TMDB id it has stored, so the button follows the saved item, not the form.
  const [storedValues, setStoredValues] = useState(null);   // the item as a refresh left it
  const stored = storedValues || item?.field_values || {};
  // An id of one of the model's sources (TMDB's for films; Open Library's, the BnF's or Google's for books).
  const schemes = presetSources(currentItemType?.preset).map(source => SOURCE_SCHEMES[source]).filter(Boolean);
  const sourceKey = (currentItemType?.fields || []).find(field => field.type === 'identifier' && schemes.includes(field.scheme) && stored[fieldKey(field)]);
  const canRefresh = !!item && !!currentItemType && schemes.length > 0
    && String(currentItemType.id) === String(item.item_type_id) && !!sourceKey;
  const refreshKeys = presetSources(currentItemType?.preset).includes('tmdb') ? 'itemForm.refresh' : 'itemForm.refreshSources';
  // Fields edited here and not saved yet: a refresh writes on the stored item, and would not show them back.
  const unsaved = canRefresh ? (currentItemType.fields || []).map(fieldKey)
    .filter(key => !sameValue(fieldValues[key], stored[key])) : [];
  const refreshFromSource = async () => {
    if (unsaved.length > 0) {
      const names = unsaved.map(key => (currentItemType.fields.find(field => fieldKey(field) === key) || {}).name || key);
      setRefreshResult({ error: t('itemForm.refresh.saveFirst', { fields: names.join(', ') }) });
      return;
    }
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const response = await api.post(`/inventories/${inventory.id}/items/${item.id}/refresh-from-source`, {});
      const { item: fresh, changed, warnings } = response.data;
      setFieldValues(prev => {
        const next = { ...prev };
        changed.forEach(key => { next[key] = fresh.field_values[key]; });
        return next;
      });
      const names = changed.map(key => (currentItemType.fields.find(field => fieldKey(field) === key) || {}).name || key);
      setStoredValues(fresh.field_values);
      // What the server wrote is saved: it is the new baseline.
      if (baseline.current) {
        const next = { ...baseline.current.values };
        changed.forEach(key => { next[key] = fresh.field_values[key]; });
        baseline.current = { ...baseline.current, values: next };
      }
      setRefreshResult({ changed: names, warnings: warnings || [] });
      if (onRefreshed) onRefreshed(fresh);
    } catch (error) {
      setRefreshResult({ error: refreshError(t, error) });
    } finally {
      setRefreshing(false);
    }
  };

  // "Find details" (phase 4 §1.7): a saved item with no source id but an ISBN or an EAN is looked up by it.
  const storedCode = item && currentItemType && String(currentItemType.id) === String(item.item_type_id)
    ? codeOfItem(currentItemType.fields, stored) : null;
  const canFind = !!item && !canRefresh && !!storedCode && schemes.length > 0;
  const findDetails = useFindDetails({
    inventoryId: inventory.id, itemId: item && item.id, itemType: currentItemType, code: storedCode, lang: uiLang(i18n),
    onDone: ({ item: fresh, changed, warnings }) => {
      setFieldValues(prev => {
        const next = { ...prev };
        changed.forEach(key => { next[key] = fresh.field_values[key]; });
        return next;
      });
      setStoredValues(fresh.field_values);
      if (baseline.current) {
        const next = { ...baseline.current.values };
        changed.forEach(key => { next[key] = fresh.field_values[key]; });
        baseline.current = { ...baseline.current, values: next };
      }
      const names = changed.map(key => (currentItemType.fields.find(field => fieldKey(field) === key) || {}).name || key);
      setRefreshResult({ changed: names, warnings: warnings || [] });
      if (onRefreshed) onRefreshed(fresh);
    },
    onError: (error) => setRefreshResult({ error: refreshError(t, error) }),
  });
  const findFirst = () => {
    if (unsavedFor(storedCode).length > 0) {
      setRefreshResult({ error: t('itemForm.refresh.saveFirst', { fields: unsavedFor(storedCode).join(', ') }) });
      return;
    }
    setRefreshResult(null);
    findDetails.refresh();
  };
  const unsavedFor = (code) => (code ? (currentItemType.fields || []).map(fieldKey)
    .filter(key => !sameValue(fieldValues[key], stored[key]))
    .map(key => (currentItemType.fields.find(field => fieldKey(field) === key) || {}).name || key) : []);

  // "Fill from the ISBN" (phase 4 §3.4): a new item of a model with sources, its ISBN (or EAN) typed first.
  const sources = useSources();
  const codeField = !item && schemes.length > 0
    ? usedFields(currentItemType?.fields || []).find(field => field.type === 'identifier' && field.scheme === 'isbn')
      || usedFields(currentItemType?.fields || []).find(field => field.type === 'identifier' && field.scheme === 'ean')
    : null;
  const typedCode = codeField ? readCode(String(fieldValues[fieldKey(codeField)] ?? '')) : null;
  const [filling, setFilling] = useState(false);
  const [fillResult, setFillResult] = useState(null);   // {filled: [names], source} | {error} | {choose: candidates} | {search: true}
  const fillFrom = async (pick) => {
    if (!typedCode) return;
    setFilling(true);
    setFillResult(null);
    try {
      const response = await api.post(`/inventories/${inventory.id}/items/fill-from-code`, {
        item_type_id: currentItemType.id, code: typedCode.value, lang: uiLang(i18n),
        ...(pick ? { source: pick.source, source_id: pick.source_id } : {}),
      });
      const { values, source, warnings } = response.data;
      const filledKeys = Object.keys(values || {}).filter(key => fieldValues.hasOwnProperty(key) && isEmptyValue(fieldValues[key]));
      setFieldValues(prev => {
        const next = { ...prev };
        filledKeys.forEach(key => { if (isEmptyValue(prev[key])) next[key] = values[key]; });
        return next;
      });
      const names = filledKeys.map(key => (currentItemType.fields.find(field => fieldKey(field) === key) || {}).name || key);
      setFillResult({ filled: names, source, warnings: warnings || [] });
    } catch (error) {
      const data = error.response?.data || {};
      if (!pick && data.code === 'ambiguous_source') setFillResult({ choose: data.candidates || [] });
      else if (!pick && data.code === 'source_not_found' && !data.source) setFillResult({ search: true, tried: data.sources_tried || [] });
      else setFillResult({ error: sourceError(t, error, currentItemType.fields || []) });
    } finally {
      setFilling(false);
    }
  };
  const [fillDialog, setFillDialog] = useState(null);   // null | {candidates?}
  const fillControls = codeField && typedCode && (
    <div className="item-form__fill">
      <Button variant="secondary" size="sm" icon={RefreshIcon} onClick={() => fillFrom(null)} loading={filling}>
        {typedCode.scheme === 'isbn' ? t('itemForm.fill.fromIsbn') : t('itemForm.fill.fromEan')}
      </Button>
      <span aria-live="polite" className="ui-field__hint">
        {fillResult && fillResult.filled && (fillResult.filled.length
          ? t('itemForm.fill.done', { count: fillResult.filled.length, source: sourceLabel(fillResult.source, sources, uiLang(i18n)) })
          : t('itemForm.fill.nothing', { source: sourceLabel(fillResult.source, sources, uiLang(i18n)) }))}
        {fillResult && fillResult.choose && t('itemForm.fill.several', { count: fillResult.choose.length })}
        {fillResult && fillResult.search && t('itemForm.fill.unknown')}
      </span>
      {fillResult && fillResult.choose && (
        <Button variant="ghost" size="sm" onClick={() => setFillDialog({ candidates: fillResult.choose })}>{t('itemForm.fill.choose')}</Button>
      )}
      {fillResult && fillResult.search && (
        <Button variant="ghost" size="sm" onClick={() => setFillDialog({})}>{t('scan.card.searchByText')}</Button>
      )}
      {fillResult && fillResult.error && <p className="ui-field__error" role="alert">{fillResult.error}</p>}
      {fillDialog && (
        <TextSearchDialog itemType={currentItemType} code={typedCode} lang={uiLang(i18n)} candidates={fillDialog.candidates}
          title={fillDialog.candidates ? t('findDetails.pickTitle') : t('findDetails.searchTitle')}
          onClose={() => setFillDialog(null)}
          onPick={(candidate) => { setFillDialog(null); fillFrom(candidate); }} />
      )}
    </div>
  );

  const renderFieldInput = (field) => {
    const key = fieldKey(field);
    const raw = fieldValues[key];
    const value = raw === undefined || raw === null ? '' : raw;
    const change = (next) => handleFieldChange(key, next);
    const inputId = `item-field-${key}`;
    const invalid = fieldErrors[key] ? true : undefined;

    switch (field.type) {
      case 'yesno':
        return <YesNoInput value={raw} onChange={change} />;
      case 'tags':
        return <TagsInput value={value} onChange={change} invalid={!!fieldErrors[key]} labelledBy={`${inputId}-label`} />;
      case 'vocab':
        return <VocabInput value={raw} onChange={change} vocabulary={field.vocabulary} labels={field.option_labels} invalid={!!fieldErrors[key]} labelledBy={`${inputId}-label`} />;
      case 'link':
        return <LinkInput value={value} onChange={change} required={field.required} labelledBy={`${inputId}-label`} />;
      case 'rating':
        return <RatingInput value={value} onChange={change} max={field.max} />;
      case 'money':
        return <MoneyInput value={value} onChange={change} field={field} required={field.required} />;
      case 'measure':
        return <MeasureInput value={value} onChange={change} field={field} required={field.required} />;
      case 'identifier':
        return <IdentifierInput value={value} onChange={change} field={field} required={field.required} labelledBy={`${inputId}-label`} />;
      case 'image':
        return <ImageInput value={raw} onChange={change} inventoryId={inventory.id} isCover={field.role === 'cover'}
          onBusy={(on) => setUploading(prev => (prev[key] === on ? prev : { ...prev, [key]: on }))} />;
      case 'textarea':
        return (
          <textarea
            id={inputId}
            aria-invalid={invalid}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            required={field.required}
            rows="3"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            id={inputId}
            aria-invalid={invalid}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            required={field.required}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            id={inputId}
            aria-invalid={invalid}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            id={inputId}
            aria-invalid={invalid}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            required={field.required}
          >
            <option value="">{t('itemForm.selectOption')}</option>
            {field.options && field.options.map((option, index) => (
              <option key={index} value={optionValue(option)}>{fieldOptionLabel(field, option, uiLang(i18n))}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="item-choices">
            {field.options && field.options.map((option, index) => (
              <label key={index} className="item-choice">
                <input
                  type="radio"
                  name={key}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  required={field.required}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div className="item-choices">
            {field.options && field.options.map((option, index) => (
              <label key={index} className="item-choice">
                <input
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkboxValues, option]
                      : checkboxValues.filter(v => v !== option);
                    handleFieldChange(key, newValues);
                  }}
                />
                {option}
              </label>
            ))}
            {field.required && checkboxValues.length === 0 && (
              <p className="ui-field__hint is-warning">
                {t('itemForm.selectAtLeastOne')}
              </p>
            )}
          </div>
        );
      default:
        return (
          <input
            type="text"
            id={inputId}
            aria-invalid={invalid}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            required={field.required}
          />
        );
    }
  };

  // Unsaved edits ask before leaving: Cancel, the page's and the shell's links,
  // the shell's search box and + button, closing the tab.
  const base = baseline.current;
  const dirty = !!base && (
    JSON.stringify(filled(fieldValues)) !== JSON.stringify(filled(base.values))
    || String(currentItemType?.id ?? '') !== String(base.typeId ?? '')
    || (hasLocations && (locationId ?? null) !== (item?.location_id ?? null))
  );
  const askToLeave = useCallback(() => confirm({
    title: t('itemForm.leave.title'),
    message: t('itemForm.leave.message'),
    confirmLabel: t('itemForm.leave.confirm'),
    cancelLabel: t('itemForm.leave.stay'),
    danger: true,
  }), [confirm, t]);
  useLeaveGuard(dirty, askToLeave);
  const cancel = async () => {
    if (!dirty || await askToLeave()) onClose();
  };
  // Deleting asks its own question; a deleted item has nothing left to save.
  const remove = () => { if (onDelete) onDelete(); };

  const collectionCrumbs = [
    { label: inventory.is_mine ? t('shell.myCollections') : t('shell.sharedWithMe'), to: '/' },
    { label: inventory.name, to: `/c/${inventory.id}` },
  ];

  // Don't render until currentItemType is set
  if (!currentItemType) {
    return (
      <div className="item-form" aria-busy="true">
        <PageHeader breadcrumb={collectionCrumbs} title={item ? t('itemForm.editTitle') : t('itemForm.newTitle')} />
        <Skeleton height={240} radius="var(--radius-lg)" />
      </div>
    );
  }

  const fields = usedFields(currentItemType?.fields || inventory.fields || []);
  const imageFields = fields.filter(field => field.type === 'image')
    .sort((a, b) => Number(b.role === 'cover') - Number(a.role === 'cover'));
  // A new item of a model with sources: its ISBN (or EAN) first, with "Fill from the ISBN" under it.
  const firstKey = codeField ? fieldKey(codeField) : null;
  const mainFields = [
    ...(codeField ? [codeField] : []),
    ...fields.filter(field => field.type !== 'image' && MAIN_ROLES.includes(field.role) && fieldKey(field) !== firstKey),
  ];
  const detailFields = fields.filter(field => field.type !== 'image' && !MAIN_ROLES.includes(field.role) && fieldKey(field) !== firstKey);
  const storedTitle = item ? itemSummary(item, currentItemType.fields || []).title : '';
  const title = item ? (storedTitle || t('itemForm.editTitle')) : t('itemForm.newTitleIn', { name: inventory.name });

  const renderField = (field) => {
    const key = fieldKey(field);
    return (
      <ItemField
        key={key}
        id={`item-field-${key}`}
        label={field.name}
        required={field.required}
        hint={field.description}
        error={fieldErrors[key]}
        simple={SIMPLE_TYPES.includes(field.type || 'text') || !field.type}
      >
        {renderFieldInput(field)}
      </ItemField>
    );
  };

  const section = (id, heading, content) => (
    <Card as="section" className="item-form__section" aria-labelledby={`item-section-${id}`}>
      <h2 id={`item-section-${id}`} className="item-form__heading">{heading}</h2>
      <div className="item-form__fields">{content}</div>
    </Card>
  );

  return (
    <form className="item-form" onSubmit={handleSubmit}>
      <PageHeader
        breadcrumb={[...collectionCrumbs, { label: item ? t('itemForm.editCrumb') : t('itemForm.newCrumb') }]}
        title={title}
        subtitle={currentItemType.name}
        actions={(canRefresh && (
          <Button variant="secondary" icon={RefreshIcon} onClick={refreshFromSource} loading={refreshing} disabled={saving}>
            {refreshing ? t('itemForm.refresh.running') : t(`${refreshKeys}.button`)}
          </Button>
        )) || (canFind && (
          <Button variant="secondary" icon={RefreshIcon} onClick={findFirst} loading={findDetails.busy} disabled={saving}>
            {findDetails.busy ? t('itemForm.refresh.running') : t('findDetails.button')}
          </Button>
        ))}
      />

      <div className="item-form__messages">
        {canRefresh && !refreshResult && <p className="ui-field__hint">{t(`${refreshKeys}.help`)}</p>}
        {canFind && !refreshResult && <p className="ui-field__hint">{t('findDetails.help')}</p>}
        {findDetails.dialog}
        {refreshResult && (
          refreshResult.error
            ? <Alert tone="danger">{refreshResult.error}</Alert>
            : (
              <Alert tone={refreshResult.changed.length === 0 ? 'info' : 'success'}>
                <p>{refreshResult.changed.length === 0
                  ? t(`${refreshKeys}.nothing`)
                  : t(`${refreshKeys}.changed`, { fields: refreshResult.changed.join(', ') })}</p>
                <SourceWarnings warnings={refreshResult.warnings} fields={currentItemType.fields} />
              </Alert>
            )
        )}
        {formError && <Alert tone="danger">{formError}</Alert>}
      </div>

      <div className="item-form__layout">
        <div className="item-form__main">
          {(availableItemTypes && availableItemTypes.length > 1) || mainFields.length > 0 ? section('main', t('itemForm.sections.main'), (
            <>
              {availableItemTypes && availableItemTypes.length > 1 && (
                <Select
                  label={t('itemForm.itemType')}
                  value={currentItemType?.id || ''}
                  hint={[currentItemType.description, t('itemForm.fieldsInType', { count: currentItemType.fields?.length || 0 })].filter(Boolean).join(' · ')}
                  onChange={(e) => {
                    const newItemType = availableItemTypes.find(type => String(type.id) === e.target.value);
                    if (newItemType) handleItemTypeChange(newItemType);
                  }}
                >
                  {availableItemTypes.map(itemType => (
                    <option key={itemType.id} value={itemType.id}>
                      {itemType.name}{itemType.is_default ? ` (${t('collections.default')})` : ''}
                    </option>
                  ))}
                </Select>
              )}
              {mainFields.map(field => (
                <React.Fragment key={fieldKey(field)}>
                  {renderField(field)}
                  {fieldKey(field) === firstKey && fillControls}
                </React.Fragment>
              ))}
            </>
          )) : null}
          {detailFields.length > 0 && section('details', mainFields.length > 0 ? t('itemForm.sections.details') : t('itemForm.sections.fields'), detailFields.map(renderField))}
        </div>

        <div className="item-form__side">
          {(imageFields.length > 0 || (item && item.source_media_id)) && section('images', t('itemForm.sections.images'), (
            <>
              {imageFields.map(renderField)}
              {item && item.source_media_id && (
                <figure className="item-form__source">
                  <a href={mediaUrl(item.source_media_id, 'original')} target="_blank" rel="noopener noreferrer">
                    <img src={mediaUrl(item.source_media_id, 'thumb')} alt={t('itemForm.sourcePhoto')} loading="lazy" />
                  </a>
                  <figcaption>{t('itemForm.sourcePhoto')}</figcaption>
                </figure>
              )}
            </>
          ))}

          {hasLocations && section('location', t('collection.location'), (
            <ItemField id="item-location" label={t('itemForm.place')} hint={t('itemForm.locationHelp')} error={fieldErrors[LOCATION_KEY]} simple>
              <LocationPicker
                inputId="item-location"
                places={places}
                value={locationId}
                onChange={(id) => {
                  setLocationId(id);
                  setFieldErrors(prev => {
                    if (!prev[LOCATION_KEY]) return prev;
                    const next = { ...prev };
                    delete next[LOCATION_KEY];
                    return next;
                  });
                }}
                onAdd={canAddPlace ? addPlace : undefined}
                invalid={!!fieldErrors[LOCATION_KEY]}
              />
            </ItemField>
          ))}
        </div>
      </div>

      <div className="item-form__bar">
        {item && onDelete && (
          <Button variant="ghost" icon={TrashIcon} className="item-form__delete" onClick={remove} disabled={saving}>
            {t('collection.deleteItem')}
          </Button>
        )}
        <span className="item-form__status" aria-live="polite">
          {busy ? t('itemForm.uploading') : dirty ? t('itemForm.unsaved') : ''}
        </span>
        <Button variant="secondary" onClick={cancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={saving} disabled={busy} title={busy ? t('itemForm.waitForUploads') : undefined}>
          {item ? t('itemForm.update') : t('itemForm.create')}
        </Button>
      </div>
    </form>
  );
}

export default ItemForm;